import dayjs, { type Dayjs } from "dayjs";

import type { ProposePayload } from "./types";

export type CompanyOption = { value: string; label: string; name: string };
export type ProductOption = { value: string; label: string; name: string };
export type ProposalStatus = "DRAFT" | "PUBLISH";
export type MobileItemEditor = {
  index: number;
  isNew: boolean;
  snapshot?: Record<string, any>;
};

export const uniqueProductOptions = (options: ProductOption[]) =>
  [...new Map(options.map((option) => [option.value, option])).values()];

export const asDay = (value?: string | null) => (value ? dayjs(value) : null);

export const disableTodayAndPast = (current: Dayjs) =>
  current && current.isBefore(dayjs().add(1, "day"), "day");

export const datesInPeriod = (period?: [Dayjs, Dayjs]) => {
  if (!period?.[0] || !period?.[1]) return [] as Dayjs[];
  const dates: Dayjs[] = [];
  let date = period[0].startOf("day");
  while (date.isBefore(period[1], "day") || date.isSame(period[1], "day")) {
    dates.push(date);
    date = date.add(1, "day");
  }
  return dates;
};

export const createScheduleRows = (dates: Dayjs[]) =>
  dates.map((delivery_date) => ({
    delivery_date,
    quantity: 0,
    wholesale_price: null,
    price: null,
  }));

export const copyProposalItem = (item: Record<string, any>) => ({
  ...item,
  schedules: item.schedules?.map((schedule: Record<string, any>) => ({
    ...schedule,
  })),
});

export const proposalProductLabel = (item: Record<string, any>) => {
  if (item.product_code && item.product_name)
    return `${item.product_code} - ${item.product_name}`;
  return item.product_name || item.product_code || "商品を選択";
};

export const formatRepresentativeId = (representativeId?: string | null) => {
  if (!representativeId) return "—";
  const [code, ...branch] = representativeId.trim().split("-");
  const formattedCode = /^\d+$/.test(code) ? code.padStart(4, "0") : code;
  return [formattedCode, ...branch].join("-");
};

const isValidDateValue = (value: unknown) =>
  value !== null &&
  value !== undefined &&
  dayjs(value as dayjs.ConfigType).isValid();

export type ProposalPayloadResult =
  | { payload: ProposePayload }
  | { errorStep: 1 | 2 | 3; message: string };

export const toProposalPayload = ({
  values,
  status,
  scheduleDates,
  isMobile,
}: {
  values: Record<string, any>;
  status: ProposalStatus;
  scheduleDates: Dayjs[];
  isMobile: boolean;
}): ProposalPayloadResult => {
  const effectivePeriod = values.effective_period as
    | [Dayjs | null | undefined, Dayjs | null | undefined]
    | undefined;
  const hasValidPeriod =
    Array.isArray(effectivePeriod) &&
    effectivePeriod.length === 2 &&
    isValidDateValue(effectivePeriod[0]) &&
    isValidDateValue(effectivePeriod[1]);
  const hasBasicInfo =
    typeof values.representative_id === "string" &&
    values.representative_id.trim().length > 0 &&
    values.recipient_id !== null &&
    values.recipient_id !== undefined &&
    hasValidPeriod &&
    isValidDateValue(values.estimated_delivery_date) &&
    isValidDateValue(values.estimated_receipt_date);

  if (!hasBasicInfo)
    return { errorStep: 1, message: "提案基本情報を確認してください" };
  if (!Array.isArray(values.items) || !values.items.length)
    return { errorStep: 2, message: "少なくとも1つの商品を追加してください" };
  if (
    values.items.some(
      (item: Record<string, any>) =>
        typeof item.product_code !== "string" ||
      !item.product_code.trim(),
    )
  )
    return { errorStep: 2, message: "商品を選択してください" };

  const { effective_period: _effectivePeriod, ...formValues } = values;
  const payload: ProposePayload = {
    order_name: values.order_name?.trim() || undefined,
    representative_id: formValues.representative_id,
    recipient_id: formValues.recipient_id,
    note: formValues.note,
    expires_at: formValues.expires_at,
    effective_from: dayjs(effectivePeriod[0]).format("YYYY-MM-DD"),
    effective_to: dayjs(effectivePeriod[1]).format("YYYY-MM-DD"),
    estimated_delivery_date: values.estimated_delivery_date?.format("YYYY-MM-DD"),
    estimated_receipt_date: values.estimated_receipt_date?.format("YYYY-MM-DD"),
    items: values.items.map((item: Record<string, any>) => ({
      product_code: item.product_code.trim(),
    })),
  };
  return { payload };
};
